/**
 * Mission Panel
 * Displays daily and weekly missions with progress tracking
 */

import { EventBus } from '../../utils/EventBus';

export interface Mission {
  id: number;
  type: 'daily' | 'weekly' | 'achievement';
  name: string;
  description: string;
  progress: number;
  required: number;
  rewardXP: number;
  rewardCredits: number;
  completed: boolean;
  claimed: boolean;
}

export class MissionPanel {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private visible: boolean = false;

  // Mission containers
  private dailyMissionsContainer!: Phaser.GameObjects.Container;
  private weeklyMissionsContainer!: Phaser.GameObjects.Container;
  private closeButton!: Phaser.GameObjects.Text;

  // Tab state
  private currentTab: 'daily' | 'weekly' = 'daily';
  private tabButtons: Map<string, Phaser.GameObjects.Text> = new Map();

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    const centerX = scene.scale.width / 2;
    const centerY = scene.scale.height / 2;

    this.container = scene.add.container(centerX, centerY);
    this.container.setDepth(2000);
    this.container.setVisible(false);

    this.createPanel();
    this.setupEventListeners();
  }

  private createPanel(): void {
    const panelWidth = 652;
    const panelHeight = 550;

    // Overlay
    const overlay = this.scene.add.rectangle(0, 0, this.scene.scale.width, this.scene.scale.height, 0x000000, 0.5);
    overlay.setInteractive();
    overlay.on('pointerdown', () => this.hide());
    this.container.add(overlay);

    // Main panel
    const bg = this.scene.add.rectangle(0, 0, panelWidth, panelHeight, 0x1a1a2e, 0.98);
    bg.setStrokeStyle(3, 0x4a5568);
    this.container.add(bg);

    // Title
    const title = this.scene.add.text(0, -panelHeight / 2 + 25, 'Missions', {
      fontSize: '20px',
      fontFamily: 'Arial',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);
    this.container.add(title);

    // Close button
    this.closeButton = this.scene.add.text(panelWidth / 2 - 25, -panelHeight / 2 + 25, '✕', {
      fontSize: '24px',
      color: '#ff6b6b',
    });
    this.closeButton.setOrigin(0.5);
    this.closeButton.setInteractive({ useHandCursor: true });
    this.closeButton.on('pointerdown', () => this.hide());
    this.closeButton.on('pointerover', () => this.closeButton.setColor('#ff0000'));
    this.closeButton.on('pointerout', () => this.closeButton.setColor('#ff6b6b'));
    this.container.add(this.closeButton);

    // Tabs
    this.createTabs(panelWidth, panelHeight);

    // Mission containers
    this.dailyMissionsContainer = this.scene.add.container(0, -panelHeight / 2 + 120);
    this.container.add(this.dailyMissionsContainer);

    this.weeklyMissionsContainer = this.scene.add.container(0, -panelHeight / 2 + 120);
    this.weeklyMissionsContainer.setVisible(false);
    this.container.add(this.weeklyMissionsContainer);

    // Info text
    const infoY = panelHeight / 2 - 40;
    const infoText = this.scene.add.text(
      0,
      infoY,
      'Complete missions to earn XP and resources\nDaily missions reset every 24 hours',
      {
        fontSize: '11px',
        color: '#888888',
        align: 'center',
      },
    );
    infoText.setOrigin(0.5);
    this.container.add(infoText);

    // Missions start empty - populated from server via 'missions_updated' event
  }

  private createTabs(_panelWidth: number, panelHeight: number): void {
    const tabY = -panelHeight / 2 + 70;
    const tabWidth = 100;
    const tabSpacing = 10;

    const tabs = [
      { key: 'daily', label: 'Daily (3)' },
      { key: 'weekly', label: 'Weekly (3)' },
    ];

    const startX = -(tabs.length * (tabWidth + tabSpacing)) / 2 + tabWidth / 2;

    tabs.forEach((tab, index) => {
      const x = startX + index * (tabWidth + tabSpacing);
      const isActive = tab.key === this.currentTab;

      const tabBg = this.scene.add.rectangle(x, tabY, tabWidth, 30, isActive ? 0x4299e1 : 0x2d3748);
      tabBg.setStrokeStyle(1, isActive ? 0x60a5fa : 0x4a5568);
      this.container.add(tabBg);

      const tabText = this.scene.add.text(x, tabY, tab.label, {
        fontSize: '12px',
        color: '#ffffff',
        fontStyle: isActive ? 'bold' : 'normal',
      });
      tabText.setOrigin(0.5);
      tabText.setInteractive({ useHandCursor: true });
      tabText.setData('tabKey', tab.key);
      tabText.setData('tabBg', tabBg);

      tabText.on('pointerdown', () => this.switchTab(tab.key as 'daily' | 'weekly'));
      tabText.on('pointerover', () => {
        if (tab.key !== this.currentTab) {
          tabBg.setFillStyle(0x3d4758);
        }
      });
      tabText.on('pointerout', () => {
        if (tab.key !== this.currentTab) {
          tabBg.setFillStyle(0x2d3748);
        }
      });

      this.container.add(tabText);
      this.tabButtons.set(tab.key, tabText);
    });
  }

  private switchTab(tab: 'daily' | 'weekly'): void {
    if (this.currentTab === tab) return;

    this.currentTab = tab;

    // Update tab appearance
    this.tabButtons.forEach((tabText, key) => {
      const tabBg = tabText.getData('tabBg') as Phaser.GameObjects.Rectangle;
      const isActive = key === tab;

      tabBg.setFillStyle(isActive ? 0x4299e1 : 0x2d3748);
      tabBg.setStrokeStyle(1, isActive ? 0x60a5fa : 0x4a5568);
      tabText.setStyle({ fontStyle: isActive ? 'bold' : 'normal' });
    });

    // Show/hide mission containers
    this.dailyMissionsContainer.setVisible(tab === 'daily');
    this.weeklyMissionsContainer.setVisible(tab === 'weekly');
  }

  private updateMissions(missions: Mission[]): void {
    const dailyMissions = missions.filter((m) => m.type === 'daily');
    const weeklyMissions = missions.filter((m) => m.type === 'weekly');

    this.updateMissionContainer(this.dailyMissionsContainer, dailyMissions);
    this.updateMissionContainer(this.weeklyMissionsContainer, weeklyMissions);
  }

  private updateMissionContainer(container: Phaser.GameObjects.Container, missions: Mission[]): void {
    container.removeAll(true);

    if (missions.length === 0) {
      const noMissionsText = this.scene.add.text(0, 100, 'No missions available', {
        fontSize: '18px',
        color: '#888888',
      });
      noMissionsText.setOrigin(0.5);
      container.add(noMissionsText);
      return;
    }

    missions.forEach((mission, index) => {
      const missionCard = this.createMissionCard(mission, index);
      container.add(missionCard);
    });
  }

  private createMissionCard(mission: Mission, index: number): Phaser.GameObjects.Container {
    const cardContainer = this.scene.add.container(0, index * 110);
    const cardWidth = 450;
    const cardHeight = 100;

    // Card background
    const bg = this.scene.add.rectangle(0, 0, cardWidth, cardHeight, mission.completed ? 0x1e3a2e : 0x2d3748);
    bg.setStrokeStyle(1, mission.completed ? 0x4ade80 : 0x4a5568);
    cardContainer.add(bg);

    // Mission name
    const name = this.scene.add.text(-cardWidth / 2 + 15, -cardHeight / 2 + 15, mission.name, {
      fontSize: '14px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    cardContainer.add(name);

    // Mission description
    const desc = this.scene.add.text(-cardWidth / 2 + 15, -cardHeight / 2 + 35, mission.description, {
      fontSize: '11px',
      color: '#cccccc',
      wordWrap: { width: cardWidth - 30 },
    });
    cardContainer.add(desc);

    // Progress bar
    const progressBarY = cardHeight / 2 - 30;
    const progressBarWidth = cardWidth - 150;

    const progressBg = this.scene.add.rectangle(-75, progressBarY, progressBarWidth, 12, 0x1a1a2e);
    progressBg.setStrokeStyle(1, 0x4a5568);
    cardContainer.add(progressBg);

    const progressPercent = Math.min(mission.progress / mission.required, 1);
    const progressFill = this.scene.add.rectangle(
      -75 - progressBarWidth / 2,
      progressBarY,
      progressBarWidth * progressPercent,
      10,
      0x4299e1,
    );
    progressFill.setOrigin(0, 0.5);
    cardContainer.add(progressFill);

    // Progress text
    const progressText = this.scene.add.text(-75, progressBarY, `${mission.progress} / ${mission.required}`, {
      fontSize: '10px',
      color: '#ffffff',
    });
    progressText.setOrigin(0.5);
    cardContainer.add(progressText);

    // Rewards
    const rewardText = this.scene.add.text(
      cardWidth / 2 - 100,
      -cardHeight / 2 + 15,
      `${mission.rewardXP} XP\n${mission.rewardCredits} 💰`,
      {
        fontSize: '11px',
        color: '#FFD700',
        align: 'right',
      },
    );
    rewardText.setOrigin(1, 0);
    cardContainer.add(rewardText);

    // Claim button or status
    if (mission.completed && !mission.claimed) {
      const claimBtn = this.scene.add.rectangle(cardWidth / 2 - 60, cardHeight / 2 - 15, 100, 25, 0x4ade80);
      claimBtn.setStrokeStyle(1, 0x22c55e);
      claimBtn.setInteractive({ useHandCursor: true });

      const claimText = this.scene.add.text(cardWidth / 2 - 60, cardHeight / 2 - 15, 'Claim Reward', {
        fontSize: '11px',
        color: '#ffffff',
      });
      claimText.setOrigin(0.5);

      claimBtn.on('pointerover', () => claimBtn.setFillStyle(0x22c55e));
      claimBtn.on('pointerout', () => claimBtn.setFillStyle(0x4ade80));
      claimBtn.on('pointerdown', () => {
        EventBus.emit('claim_mission_reward', { missionId: mission.id });
      });

      cardContainer.add(claimBtn);
      cardContainer.add(claimText);
    } else if (mission.claimed) {
      const claimedText = this.scene.add.text(cardWidth / 2 - 60, cardHeight / 2 - 15, '✓ Claimed', {
        fontSize: '12px',
        color: '#4ade80',
      });
      claimedText.setOrigin(0.5);
      cardContainer.add(claimedText);
    }

    return cardContainer;
  }

  private setupEventListeners(): void {
    EventBus.on('missions_updated', this.updateMissions, this);
  }

  public show(): void {
    this.visible = true;
    this.container.setVisible(true);

    // Request missions from server (TODO: implement when server has mission endpoints)
    // EventBus.emit('request_missions', {});

    // Fade in
    this.container.setAlpha(0);
    this.scene.tweens.add({
      targets: this.container,
      alpha: 1,
      duration: 200,
      ease: 'Power2',
    });

    EventBus.on('close-topmost-dialog', this.hide, this);
  }

  public hide(): void {
    EventBus.off('close-topmost-dialog', this.hide, this);
    this.visible = false;

    // Fade out
    this.scene.tweens.add({
      targets: this.container,
      alpha: 0,
      duration: 200,
      ease: 'Power2',
      onComplete: () => {
        this.container.setVisible(false);
      },
    });
  }

  public isVisible(): boolean {
    return this.visible;
  }

  public destroy(): void {
    EventBus.off('missions_updated', this.updateMissions, this);
    this.tabButtons.clear();
    this.container.destroy();
  }
}
